import React, { useState, useEffect } from 'react';
import { DownloadIcon } from 'lucide-react';
import { createClient } from '../../lib/supabase/client';
import {
  createSignedNoteUrl,
  getFileIcon,
  formatFileSize,
} from '../../lib/storage/note-files';

interface FileAttachmentsProps {
  fileUrls?: string[];
  fileNames?: string[];
  fileSizes?: number[];
}

interface FileData {
  url: string;
  name: string;
  size: number;
  signedUrl: string | null;
}

export function FileAttachments({
  fileUrls = [],
  fileNames = [],
  fileSizes = [],
}: FileAttachmentsProps) {
  const [files, setFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fileUrls.length) return;

    const loadSignedUrls = async () => {
      setLoading(true);
      const supabase = createClient();

      const filesData = await Promise.all(
        fileUrls.map(async (url, index) => {
          const signedUrl = await createSignedNoteUrl(supabase, url);
          return {
            url,
            name: fileNames[index] || 'Unknown file',
            size: fileSizes[index] || 0,
            signedUrl,
          };
        })
      );

      setFiles(filesData);
      setLoading(false);
    };

    loadSignedUrls();
  }, [fileUrls, fileNames, fileSizes]);

  if (!fileUrls.length) return null;

  if (loading) {
    return (
      <div className="mt-3 text-sm text-gray-500">
        Loading attachments...
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
        Attachments ({files.length})
      </p>
      <div className="space-y-2">
        {files.map((file, index) => (
          <a
            key={`${file.url}-${index}`}
            href={file.signedUrl || '#'}
            download={file.name}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group ${
              !file.signedUrl ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={(e) => {
              if (!file.signedUrl) {
                e.preventDefault();
              }
            }}
          >
            <span className="text-2xl flex-shrink-0">
              {getFileIcon(file.name)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#212121] truncate">
                {file.name}
              </p>
              {file.size > 0 && (
                <p className="text-xs text-gray-600">
                  {formatFileSize(file.size)}
                </p>
              )}
            </div>
            {file.signedUrl && (
              <DownloadIcon className="w-4 h-4 text-gray-400 group-hover:text-[#5B8E87] flex-shrink-0 transition-colors" />
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
